import { LitElement, html, property } from 'lit-element'
import { ifDefined } from 'lit-html/directives/if-defined'
import { register } from '@polleverywhere/hmr-custom-element'
import { bemBlock } from '@polleverywhere/bem-helpers'
import { cutlassEvent, contextualize } from '@polleverywhere/cutlass'
import env from '@polleverywhere/core/lib/env'
import { pluralize } from '@polleverywhere/core/lib/util'
import '@polleverywhere/activity-visualization'
import { LINKS, ICONS } from './constants'
import './style.scss'

/**
 * `pe-webex-home` is the landing screen shown to presenters when
 * they're inside the webex integration.
 *
 * When no activity is set on the component, it shows support materials.
 *
 * When an activity is set on the component, it shows the activity's
 * title (with a link to its viz page) and visualization.
 *
 * @element pe-webex-home
 *
 * @prop {Object} activity - Contains attributes for the currently
 *                           active activity (null if nothing is active)
 *
 * @fires click:link - Fires when an url is clicked.
 * @fires click:logout - Fires when the logout button is clicked.
 */
export default class PeWebexHome extends LitElement {
  static get tagName () { return 'pe-webex-home' }

  @property({ type: Object, attribute: false })
  activity

  connectedCallback () {
    super.connectedCallback()
    contextualize(this, this.tagName)
  }

  createRenderRoot () { return this }

  render () {
    return html`
      <div class=${bem('main')}>
        <div class=${bem('section')}>
          ${renderStatus.call(this)}
          ${renderActivity.call(this)}
        </div>
        ${renderContent.call(this)}
      </div>

      <div class=${bem('footer')}>
        ${renderLink.call(this, LINKS.support)}
        ${renderLink.call(this, LINKS.logout)}
      </div>
    `
  }
}

function renderStatus () {
  const iconName = this.activity ? 'activate' : 'activate-off'
  const message = this.activity
    ? 'You are presenting...'
    : 'No activities are currently activated.'
  const linkOpts = {
    url: LINKS.activities.url,
    iconName: 'pop-out',
    text: 'Choose an activity to activate'
  }

  const clickOpenTogether = html`
    <br>
    <div>Click the <strong>Open Together</strong> button at the bottom of this panel to receive responses in Webex.</div>
  `

  return html`
    <div class=${bem('status', { active: !!this.activity })}>
      <pe-graphic type='icon' name=${iconName}></pe-graphic>
      <span>${message}</span>
    </div>
    ${renderLink.call(this, linkOpts)}
    ${!this.activity ? clickOpenTogether : ''}
  `
}

function renderActivity () {
  if (!this.activity) return

  const url = env.get('viz_url')
    .segment(pluralize(2, this.activity.type()))
    .segment(this.activity.get('presenter_key'))
    .query('preview=true&controls=none')
    .toString()
  const linkOpts = { url, iconName: 'pop-out', text: 'Pop out live chart' }

  return html`
    <div class=${bem('activity')}>
      <pe-graphic
        type='icon'
        name=${ICONS[this.activity.get('type')]}
      ></pe-graphic>
      ${this.activity.get('title')}
      <activity-visualization
        .model=${this.activity}
        voting-instructions='false'
        aspect-ratio=1.6
      ></activity-visualization>
      ${renderLink.call(this, linkOpts)}
      <p>
        To receive responses in Webex, ensure the <strong>Open
        together</strong> button at the bottom of this panel is selected.
      </p>
    </div>
  `
}

function renderContent () {
  return html`
    ${this.activity
      ? html`
          <div class=${bem('section')}>
            ${renderLink.call(this, LINKS.activities)}
          </div>
        `
      : html`
          <div class=${bem('section')}>
            <div class=${bem('section__title')}>
              Present your Poll Everywhere activities
            </div>
            <div class=${bem('section__body')}>
              <p>
                To display results in your meeting, share your screen with the
                browser window or slideware platform where your activity is
                embedded.
              </p>
            </div>
          </div>
          <div class=${bem('section')}>
            ${renderLink(LINKS.guide)}
          </div>
        `
      }
    </div>
  `
}

function renderLink (opts) {
  return html`
    <a @click=${handleClick} href=${ifDefined(opts.url)} target='_blank'>
      <pe-graphic type='icon' name=${opts.iconName}></pe-graphic>
      ${opts.text}
    </a>
  `
}

function handleClick (e) {
  const url = e?.target?.href
  if (url) {
    e.preventDefault()
    handleLink.call(this, url)
  } else {
    e.preventDefault() // stop link behavior if there's no href
    handleLogout.call(this)
  }
}

function handleLink (url) {
  window.open(url, '', 'width=1400, height=900')
  cutlassEvent(this, 'click:link', { url })
  this.dispatchEvent(new CustomEvent('click:link', {
    bubbles: true,
    detail: { url }
  }))
}

function handleLogout () {
  cutlassEvent(this, 'click:logout')
  this.dispatchEvent(new CustomEvent('click:logout', {
    bubbles: true,
    detail: { name: 'logout' }
  }))
}

const bem = bemBlock(PeWebexHome.tagName)
register(module, PeWebexHome.tagName, PeWebexHome)
